// Purely decorative backdrop for the auth screen: a faint clock face resting
// in the top-left corner and a small week grid in the bottom-right, framing
// the page diagonally. Both are desktop-only accents; positioning/opacity is
// inline (like the page background) so nothing depends on the prebuilt
// Tailwind CSS.
const CX = 190;
const CY = 190;
const RIM = 140;

const MINUTE_TICKS = Array.from({ length: 60 }, (_, i) => (i * Math.PI) / 30);
const HOUR_TICKS = Array.from({ length: 12 }, (_, i) => (i * Math.PI) / 6);

const pointAt = (angle: number, radius: number): { x: number; y: number } => ({
  x: CX + Math.sin(angle) * radius,
  y: CY - Math.cos(angle) * radius,
});

const arcPath = (from: number, to: number, radius: number): string => {
  const start = pointAt(from, radius);
  const end = pointAt(to, radius);
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 1 ${end.x} ${end.y}`;
};

// Quarter past four, with the 4-to-5 slot blocked out on the rim like a
// booked session.
const HOUR_HAND_ANGLE = (4.25 * Math.PI) / 6;
const MINUTE_HAND_ANGLE = Math.PI / 2;
const SECOND_HAND_ANGLE = (13 * Math.PI) / 12;
const BOOKED_HOUR_START = (4 * Math.PI) / 6;

const hand = (
  angle: number,
  length: number,
  tail: number,
  width: number,
  color: string,
) => {
  const tip = pointAt(angle, length);
  const back = pointAt(angle + Math.PI, tail);
  return (
    <line
      x1={back.x}
      y1={back.y}
      x2={tip.x}
      y2={tip.y}
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
    />
  );
};

const NUMERALS: { label: string; angle: number }[] = [
  { label: "12", angle: 0 },
  { label: "3", angle: Math.PI / 2 },
  { label: "6", angle: Math.PI },
  { label: "9", angle: (3 * Math.PI) / 2 },
];

const CornerClock = () => (
  <svg
    viewBox="0 0 380 380"
    className="hidden lg:block"
    style={{ position: "absolute", top: 64, left: 44, width: 220, height: 220, opacity: 0.7 }}
  >
    <defs>
      <linearGradient id="auth-clock-rim" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="rgba(196, 167, 255, 0.26)" />
        <stop offset="55%" stopColor="rgba(255, 255, 255, 0.10)" />
        <stop offset="100%" stopColor="rgba(255, 255, 255, 0.04)" />
      </linearGradient>
    </defs>

    <circle cx={CX} cy={CY} r={RIM} fill="none" stroke="url(#auth-clock-rim)" strokeWidth="1.5" />
    <circle
      cx={CX}
      cy={CY}
      r="94"
      fill="none"
      stroke="rgba(196, 167, 255, 0.07)"
      strokeWidth="1"
      strokeDasharray="3 9"
    />

    {/* The current hour, blocked out on the rim like a booked session */}
    <path
      d={arcPath(BOOKED_HOUR_START, BOOKED_HOUR_START + Math.PI / 6, 127)}
      fill="none"
      stroke="rgba(139, 92, 246, 0.30)"
      strokeWidth="8"
      strokeLinecap="round"
    />

    {MINUTE_TICKS.map((angle, i) => {
      if (i % 5 === 0) return null;
      const outer = pointAt(angle, RIM);
      const inner = pointAt(angle, RIM - 4);
      return (
        <line
          key={angle}
          x1={inner.x}
          y1={inner.y}
          x2={outer.x}
          y2={outer.y}
          stroke="rgba(255, 255, 255, 0.06)"
          strokeWidth="1"
        />
      );
    })}
    {HOUR_TICKS.map((angle) => {
      const outer = pointAt(angle, RIM);
      const inner = pointAt(angle, RIM - 9);
      return (
        <line
          key={angle}
          x1={inner.x}
          y1={inner.y}
          x2={outer.x}
          y2={outer.y}
          stroke="rgba(255, 255, 255, 0.16)"
          strokeWidth="1.5"
        />
      );
    })}

    {NUMERALS.map(({ label, angle }) => {
      const pos = pointAt(angle, 113);
      return (
        <text
          key={label}
          x={pos.x}
          y={pos.y}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="Lora, Georgia, serif"
          fontSize="18"
          fill="rgba(255, 255, 255, 0.13)"
        >
          {label}
        </text>
      );
    })}

    {/* Layered hands (wide faint stroke + bright core) at quarter past four */}
    {hand(HOUR_HAND_ANGLE, 64, 12, 5, "rgba(196, 167, 255, 0.12)")}
    {hand(HOUR_HAND_ANGLE, 64, 12, 2, "rgba(196, 167, 255, 0.30)")}
    {hand(MINUTE_HAND_ANGLE, 98, 16, 4, "rgba(196, 167, 255, 0.09)")}
    {hand(MINUTE_HAND_ANGLE, 98, 16, 1.5, "rgba(196, 167, 255, 0.24)")}
    {hand(SECOND_HAND_ANGLE, 106, 20, 1, "rgba(20, 184, 156, 0.26)")}

    <circle cx={CX} cy={CY} r="5" fill="none" stroke="rgba(196, 167, 255, 0.28)" strokeWidth="1.5" />
    <circle cx={CX} cy={CY} r="1.8" fill="rgba(196, 167, 255, 0.38)" />
  </svg>
);

// Week grid: 7 columns × 4 rows with a weekday-dash header.
const GRID_X = 9;
const GRID_Y = 22;
const CELL = 22;
const PITCH = 30;

const WEEK_CELLS = Array.from({ length: 28 }, (_, i) => ({
  x: GRID_X + (i % 7) * PITCH,
  y: GRID_Y + Math.floor(i / 7) * PITCH,
}));

// Cells that read as planned sessions, and the one that reads as today.
const SESSION_CELLS = [3, 9, 17, 24];
const TODAY_CELL = 16;

const CornerWeekGrid = () => (
  <svg
    viewBox="0 0 220 140"
    className="hidden lg:block"
    style={{ position: "absolute", bottom: 56, right: 56, width: 176, height: 112, opacity: 0.7 }}
  >
    {Array.from({ length: 7 }, (_, i) => (
      <line
        key={i}
        x1={GRID_X + i * PITCH + 5}
        y1={GRID_Y - 12}
        x2={GRID_X + i * PITCH + CELL - 5}
        y2={GRID_Y - 12}
        stroke="rgba(255, 255, 255, 0.12)"
        strokeWidth="2"
        strokeLinecap="round"
      />
    ))}
    {WEEK_CELLS.map((cell, i) => {
      const isSession = SESSION_CELLS.includes(i);
      const isToday = i === TODAY_CELL;
      return (
        <rect
          key={i}
          x={cell.x}
          y={cell.y}
          width={CELL}
          height={CELL}
          rx="6"
          fill={isSession ? "rgba(139, 92, 246, 0.18)" : "none"}
          stroke={
            isSession
              ? "rgba(196, 167, 255, 0.30)"
              : isToday
                ? "rgba(20, 184, 156, 0.32)"
                : "rgba(255, 255, 255, 0.08)"
          }
          strokeWidth={isToday ? 1.5 : 1}
        />
      );
    })}
  </svg>
);

const AuthDecor = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0 overflow-hidden"
  >
    <CornerClock />
    <CornerWeekGrid />
  </div>
);

export default AuthDecor;
